// frontend/packages/business-console/src/hooks/useBulkMediaUpload.js
//
// Upload many files at once: a small queue (3 in flight) over
// uploadFileToCloudinary, persisting a MediaAsset for each file as soon as
// its upload lands (same synchronous path the widget flow uses, so a
// missing webhook never loses an asset). Each file gets its own status, so
// one bad file never blocks the rest of the batch.

import { useCallback, useRef, useState } from 'react';
import { uploadFileToCloudinary } from '../services/cloudinaryDirectUpload';
import { createMediaAssetFromCloudinary } from '../services/mediaService';

const CONCURRENCY = 3;
const MAX_FILE_BYTES = 100 * 1024 * 1024; // Cloudinary's default per-file cap

let seq = 0;
const nextId = () => `upl-${Date.now()}-${(seq += 1)}`;

function isAcceptedType(file) {
  const t = String(file?.type || '');
  return t.startsWith('image/') || t.startsWith('video/');
}

export default function useBulkMediaUpload({ onAssetCreated } = {}) {
  const [queue, setQueue] = useState([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const runningRef = useRef(false);
  const pendingRef = useRef([]);
  const onCreatedRef = useRef(onAssetCreated);
  onCreatedRef.current = onAssetCreated;

  const patch = useCallback((id, changes) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const runOne = useCallback(
    async (entry) => {
      patch(entry.id, { status: 'uploading', progress: 0 });
      try {
        const info = await uploadFileToCloudinary(entry.file, {
          onProgress: (p) => patch(entry.id, { progress: p }),
        });
        const asset = await createMediaAssetFromCloudinary(info);
        patch(entry.id, {
          status: 'done',
          progress: 1,
          publicId: info.public_id,
          asset,
        });
        onCreatedRef.current?.(asset);
      } catch (e) {
        patch(entry.id, { status: 'error', error: e?.message || String(e) });
      }
    },
    [patch]
  );

  const drain = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (pendingRef.current.length) {
          const entry = pendingRef.current.shift();
          // eslint-disable-next-line no-await-in-loop
          await runOne(entry);
        }
      });
      await Promise.all(workers);
    } finally {
      runningRef.current = false;
    }
    // Files added while the last workers were finishing.
    if (pendingRef.current.length) drain();
  }, [runOne]);

  const addFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const entries = files.map((file) => {
        let error = '';
        if (!isAcceptedType(file)) error = 'Only images and videos can be uploaded.';
        else if (file.size > MAX_FILE_BYTES) error = 'File is larger than 100 MB.';
        return {
          id: nextId(),
          file,
          name: file.name || 'upload',
          status: error ? 'error' : 'queued',
          progress: 0,
          error,
          publicId: '',
          asset: null,
        };
      });

      setQueue((q) => [...q, ...entries]);
      pendingRef.current.push(...entries.filter((e) => e.status === 'queued'));
      drain();
    },
    [drain]
  );

  const retry = useCallback(
    (id) => {
      // Read the entry from the ref, not inside a setState updater: updaters
      // can run twice under StrictMode and would enqueue the file twice.
      const entry = queueRef.current.find((it) => it.id === id);
      if (!entry || entry.status !== 'error' || !isAcceptedType(entry.file)) return;
      if (entry.file.size > MAX_FILE_BYTES) return;
      patch(id, { status: 'queued', error: '', progress: 0 });
      pendingRef.current.push(entry);
      drain();
    },
    [drain, patch]
  );

  const clearFinished = useCallback(() => {
    setQueue((q) => q.filter((it) => it.status === 'queued' || it.status === 'uploading'));
  }, []);

  const busy = queue.some((it) => it.status === 'queued' || it.status === 'uploading');

  return { queue, busy, addFiles, retry, clearFinished };
}
